import { CheckCircle, FlaskConical, Leaf, Award, Shield } from 'lucide-react';

const qualities = [
  { icon: FlaskConical, title: "99% Purity", description: "Lab-tested for highest quality standards" },
  { icon: Leaf, title: "Canadian Made", description: "Proudly manufactured in Canada" },
  { icon: Award, title: "Lab Tested", description: "Third-party verified for quality assurance" },
  { icon: Shield, title: "Research Grade", description: "Premium compounds for scientific use" },
];

export const AboutPage = () => {
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="about-page">
      <title>About Us | Canadian Peptides | Canadian Wellness &amp; Fitness</title>
      <meta name="description" content="Canadian Wellness & Fitness is a Canadian supplier of research peptides for research and educational purposes, shipping Canada-wide by Express Post." />
      <link rel="canonical" href="https://canadianwellness.net/about" />
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="font-['Oswald'] text-4xl sm:text-5xl lg:text-6xl font-bold uppercase tracking-tighter text-white mb-6">
              🇨🇦 <span className="text-[#C41E3A]">Canadian Wellness</span> & Fitness
            </h1>
          </div>
          
          <div className="max-w-3xl mx-auto text-left">
            <p className="text-[#D4AF37] text-xl font-semibold mb-4">
              Proudly Canadian 🇨🇦
            </p>
            <p className="text-gray-300 text-lg mb-6">
              Canadian owned. Canadian based. Canadian made.
            </p>
            <p className="text-gray-300 leading-relaxed mb-8">
              At Canadian Wellness & Fitness, we are committed to providing high-quality research peptides to support health, fitness, and performance goals.
            </p>
            
            <h3 className="text-white text-xl font-semibold mb-4">✨ What are peptides?</h3>
            <p className="text-gray-300 leading-relaxed mb-4">
              Peptides are short chains of amino acids that act as signaling molecules in the body. They play a role in:
            </p>
            <ul className="text-gray-300 space-y-2 mb-8 ml-4">
              <li>• Muscle growth & recovery</li>
              <li>• Fat metabolism & lean body composition</li>
              <li>• Cellular repair</li>
              <li>• Energy & performance</li>
              <li>• Healthy aging support</li>
            </ul>
            
            <p className="text-gray-300 leading-relaxed mb-8">
              When paired with proper nutrition, training, and lifestyle habits, peptides can be part of a comprehensive health and fitness strategy.
            </p>
            
            <p className="text-gray-300 leading-relaxed mb-8">
              We believe in education, quality standards, and transparency.
            </p>
            
            <p className="text-[#D4AF37] leading-relaxed">
              📩 If you have questions or would like more information, feel free to message us directly. We are happy to guide you in the right direction.
            </p>
          </div>
        </div>
      </section>

      {/* What Are Peptides Section */}
      <section className="py-20 md:py-32 bg-[#050505]" data-testid="what-are-peptides">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6">
                What Are <span className="text-[#C41E3A]">Peptides?</span>
              </h2>
              <div className="space-y-4 text-gray-300">
                <p className="text-lg leading-relaxed">
                  Peptides are short chains of amino acids that act as messengers 
                  in the body. They help signal repair, recovery, balance, and 
                  performance throughout your biological systems.
                </p>
                <p className="leading-relaxed">
                  Your body already produces peptides naturally. These compounds 
                  play crucial roles in various physiological processes, from 
                  hormone regulation to tissue repair.
                </p>
                <ul className="space-y-3 mt-6">
                  <li className="flex items-center">
                    <CheckCircle size={20} className="text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span>Short chains of amino acids</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span>Act as messengers in the body</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span>Signal repair, recovery & performance</span>
                  </li>
                  <li className="flex items-center">
                    <CheckCircle size={20} className="text-[#D4AF37] mr-3 flex-shrink-0" />
                    <span>Naturally produced by your body</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="quality-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-4">
              Why Choose <span className="text-[#C41E3A]">Us</span>
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Our commitment to quality and purity sets the standard in research peptides.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualities.map((quality, index) => (
              <div
                key={quality.title}
                className="bg-[#121212] border border-[#27272A] p-6 rounded-sm text-center hover:border-[#C41E3A] transition-colors"
                data-testid={`quality-${index}`}
              >
                <quality.icon size={40} className="text-[#C41E3A] mx-auto mb-4" />
                <h3 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-2">
                  {quality.title}
                </h3>
                <p className="text-gray-400 text-sm">
                  {quality.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 md:py-32 bg-[#050505]" data-testid="about-benefits">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-tight text-white mb-6 text-center">
              Potential <span className="text-[#C41E3A]">Benefits</span>
            </h2>
            <div className="space-y-4">
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">⚡</span>
                  <div>
                    <h4 className="text-white font-semibold">Energy & Vitality</h4>
                    <p className="text-gray-400 text-sm">Support natural energy production</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">💪</span>
                  <div>
                    <h4 className="text-white font-semibold">Muscle Recovery & Performance</h4>
                    <p className="text-gray-400 text-sm">Enhanced recovery support</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">🔥</span>
                  <div>
                    <h4 className="text-white font-semibold">Fat Metabolism Support</h4>
                    <p className="text-gray-400 text-sm">Healthy metabolic function</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">✨</span>
                  <div>
                    <h4 className="text-white font-semibold">Skin & Anti-Aging Support</h4>
                    <p className="text-gray-400 text-sm">Promote skin health</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">🌙</span>
                  <div>
                    <h4 className="text-white font-semibold">Improved Sleep & Recovery</h4>
                    <p className="text-gray-400 text-sm">Better rest and restoration</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <span className="text-[#D4AF37] text-xl mr-3">🦴</span>
                  <div>
                    <h4 className="text-white font-semibold">Joint & Connective Tissue Support</h4>
                    <p className="text-gray-400 text-sm">Mobility and flexibility</p>
                  </div>
                </div>
              </div>
            <p className="text-gray-500 text-sm mt-6 italic text-center">
              Benefits vary by peptide & individual goals
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
